#define GLSLIFY 1
float exponentialIn(float t) {
  return t == 0.0 ? t : pow(2.0, 10.0 * (t - 1.0));
}

float exponentialOut(float t) {
  return t == 1.0 ? t : 1.0 - pow(2.0, -10.0 * t);
}

#ifndef HALF_PI
#define HALF_PI 1.5707963267948966
#endif

float sineIn(float t) {
  return sin((t - 1.0) * HALF_PI) + 1.0;
}

float quarticInOut(float t) {
  return t < 0.5
    ? +8.0 * pow(t, 4.0)
    : -8.0 * pow(t - 1.0, 4.0) + 1.0;
}

//
// GLSL textureless classic 3D noise "cnoise",
// with an RSL-style periodic variant "pnoise".
// Author:  Stefan Gustavson (stefan.gustavson@liu.se)
// Version: 2011-10-11
//
// Many thanks to Ian McEwan of Ashima Arts for the
// ideas for permutation and gradient selection.
//
// Copyright (c) 2011 Stefan Gustavson. All rights reserved.
// Distributed under the MIT license. See LICENSE file.
// https://github.com/ashima/webgl-noise
//

vec3 mod289(vec3 x)
{
  return x - floor(x * (1.0 / 289.0)) * 289.0;
}

vec4 mod289(vec4 x)
{
  return x - floor(x * (1.0 / 289.0)) * 289.0;
}

vec4 permute(vec4 x)
{
  return mod289(((x*34.0)+1.0)*x);
}

vec4 taylorInvSqrt(vec4 r)
{
  return 1.79284291400159 - 0.85373472095314 * r;
}

vec3 fade(vec3 t) {
  return t*t*t*(t*(t*6.0-15.0)+10.0);
}

// Classic Perlin noise
float cnoise(vec3 P)
{
  vec3 Pi0 = floor(P); // Integer part for indexing
  vec3 Pi1 = Pi0 + vec3(1.0); // Integer part + 1
  Pi0 = mod289(Pi0);
  Pi1 = mod289(Pi1);
  vec3 Pf0 = fract(P); // Fractional part for interpolation
  vec3 Pf1 = Pf0 - vec3(1.0); // Fractional part - 1.0
  vec4 ix = vec4(Pi0.x, Pi1.x, Pi0.x, Pi1.x);
  vec4 iy = vec4(Pi0.yy, Pi1.yy);
  vec4 iz0 = Pi0.zzzz;
  vec4 iz1 = Pi1.zzzz;

  vec4 ixy = permute(permute(ix) + iy);
  vec4 ixy0 = permute(ixy + iz0);
  vec4 ixy1 = permute(ixy + iz1);

  vec4 gx0 = ixy0 * (1.0 / 7.0);
  vec4 gy0 = fract(floor(gx0) * (1.0 / 7.0)) - 0.5;
  gx0 = fract(gx0);
  vec4 gz0 = vec4(0.5) - abs(gx0) - abs(gy0);
  vec4 sz0 = step(gz0, vec4(0.0));
  gx0 -= sz0 * (step(0.0, gx0) - 0.5);
  gy0 -= sz0 * (step(0.0, gy0) - 0.5);

  vec4 gx1 = ixy1 * (1.0 / 7.0);
  vec4 gy1 = fract(floor(gx1) * (1.0 / 7.0)) - 0.5;
  gx1 = fract(gx1);
  vec4 gz1 = vec4(0.5) - abs(gx1) - abs(gy1);
  vec4 sz1 = step(gz1, vec4(0.0));
  gx1 -= sz1 * (step(0.0, gx1) - 0.5);
  gy1 -= sz1 * (step(0.0, gy1) - 0.5);

  vec3 g000 = vec3(gx0.x,gy0.x,gz0.x);
  vec3 g100 = vec3(gx0.y,gy0.y,gz0.y);
  vec3 g010 = vec3(gx0.z,gy0.z,gz0.z);
  vec3 g110 = vec3(gx0.w,gy0.w,gz0.w);
  vec3 g001 = vec3(gx1.x,gy1.x,gz1.x);
  vec3 g101 = vec3(gx1.y,gy1.y,gz1.y);
  vec3 g011 = vec3(gx1.z,gy1.z,gz1.z);
  vec3 g111 = vec3(gx1.w,gy1.w,gz1.w);

  vec4 norm0 = taylorInvSqrt(vec4(dot(g000, g000), dot(g010, g010), dot(g100, g100), dot(g110, g110)));
  g000 *= norm0.x;
  g010 *= norm0.y;
  g100 *= norm0.z;
  g110 *= norm0.w;
  vec4 norm1 = taylorInvSqrt(vec4(dot(g001, g001), dot(g011, g011), dot(g101, g101), dot(g111, g111)));
  g001 *= norm1.x;
  g011 *= norm1.y;
  g101 *= norm1.z;
  g111 *= norm1.w;

  float n000 = dot(g000, Pf0);
  float n100 = dot(g100, vec3(Pf1.x, Pf0.yz));
  float n010 = dot(g010, vec3(Pf0.x, Pf1.y, Pf0.z));
  float n110 = dot(g110, vec3(Pf1.xy, Pf0.z));
  float n001 = dot(g001, vec3(Pf0.xy, Pf1.z));
  float n101 = dot(g101, vec3(Pf1.x, Pf0.y, Pf1.z));
  float n011 = dot(g011, vec3(Pf0.x, Pf1.yz));
  float n111 = dot(g111, Pf1);

  vec3 fade_xyz = fade(Pf0);
  vec4 n_z = mix(vec4(n000, n100, n010, n110), vec4(n001, n101, n011, n111), fade_xyz.z);
  vec2 n_yz = mix(n_z.xy, n_z.zw, fade_xyz.y);
  float n_xyz = mix(n_yz.x, n_yz.y, fade_xyz.x);
  return 2.2 * n_xyz;
}

uniform float uScale;
uniform float uPointScale;
uniform float uMapElevationFactor;
uniform float uMapScale;
uniform float uMapAmount;
uniform float uResolution;
uniform float uTime;
uniform float uDissolve;
uniform vec3 uColor;
uniform float uCameraDistance;
uniform sampler2D uElevation;

attribute float index;
attribute float colorOffset;

varying vec3 vColor;
varying float vOpacity;

float rand(vec2 co){
    return fract(sin(dot(co, vec2(12.9898, 78.233))) * 43758.5453);
}

vec3 white = vec3(1.0,1.0,1.0);

void main() {
    float random = rand(position.xy);
    float elapsed = fract((random * 10.0 + uTime * 0.00005) * random);
    elapsed += sineIn(uDissolve);
    elapsed = clamp(elapsed,0.0,1.0);
    float eased = exponentialIn(elapsed);

    float u = mod(index, uResolution);
    float v = floor(index / uResolution);

    float relU = u / uResolution;
    float relV = v / uResolution;
    float distToCenter = sineIn(sqrt(pow(relU * 2.0 - 1.0,2.0) + pow(relV * 2.0- 1.0,2.0)) / 1.414213);

    float amount = quarticInOut(clamp(distToCenter + (uMapAmount * 2.0 - 1.0), 0.0, 1.0));

    float elevation = amount == 0.0 ? 0.0 : texture2D(uElevation, vec2(u /uResolution,v/uResolution)).r;

    float spherePointSize = 0.035 + random * 0.02;
    float mapPointSize = 0.035 + random * 0.02;
    float size = mix(spherePointSize, mapPointSize, amount);

    vec3 noiseInput = mix(position * 2.0 + uTime * 0.0002, vec3(relU,0.0,relV) + uTime * 0.0001, amount);
    float n = cnoise(noiseInput) ;

    vec3 sphereStartPos = position * (1.0 + n * 0.02) * (0.9 + random * 0.1);
    vec3 mapStartPos = vec3((relU * 2.0 - 1.0), elevation * uMapElevationFactor, (relV * 2.0 - 1.0) * -1.0) * uMapScale;
    mapStartPos.x += (random * 2.0 - 1.0) * 0.01;
    mapStartPos.y += n * 0.25;
    mapStartPos.z += (random * 2.0 - 1.0) * 0.01;

    vec3 startPos = mix(sphereStartPos, mapStartPos, amount);

    vec3 sphereEndPos = position * (1.0 + exponentialIn(random) * 1.5);
    vec3 mapEndPos = mapStartPos + vec3(0.0,1.0,0.0) * exponentialIn(random);

    vec3 endPos = mix(sphereEndPos, mapEndPos, amount);
    vec3 transformed = mix(startPos, endPos, eased);
    transformed += n * eased * 0.35 * (1.0 - amount * 0.5);

    vec4 mvPosition = modelViewMatrix * vec4(transformed, 1);

    float fog = (1.0 - clamp(mvPosition.z + uCameraDistance + 0.25 , 0.0, 1.0)) * 0.75 * (1.0 - amount);
    float offset = clamp(colorOffset - fog, 0.0,1.0) * (1.0 - amount * 0.5);
    vColor = mix(uColor, white, offset);

    vOpacity = elapsed < 0.5 ? exponentialOut(elapsed * 2.0) : 1.0 - exponentialIn(elapsed);

    gl_PointSize = size * uPointScale;
    gl_PointSize *= ( uScale / - mvPosition.z );

    gl_Position = projectionMatrix * mvPosition;
}